import React, { useState } from 'react';
import { hotelData } from '../data/hotelData';
import * as LucideIcons from 'lucide-react';

const Amenities = () => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  // A luxurious hotel interior background for the title column/section base
  const bgImage = "https://images.unsplash.com/photo-1542314831-c6a4d14b8fcb?auto=format&fit=crop&w=1920&q=80";

  return (
    <section id="amenities" className="relative w-full h-auto md:h-[600px] overflow-hidden bg-luxury-black">
      {/* Background Image for the whole section (mostly visible behind the title) */}
      <div className="absolute inset-0 z-0">
        <img 
          src={bgImage} 
          alt="Luxury Amenities" 
          className="w-full h-full object-cover opacity-40"
        />
      </div>

      {/* Main Container - Flex row on desktop, column on mobile */}
      <div className="relative z-10 w-full h-full flex flex-col md:flex-row">
        
        {/* Title Column */}
        <div className="w-full md:w-[35%] lg:w-[30%] bg-black/70 md:border-r border-white/20 p-8 md:p-12 flex flex-col justify-center border-b md:border-b-0 backdrop-blur-md">
          <span className="text-orange-400 font-medium tracking-widest uppercase text-xs mb-4 block">
            World-Class Service
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl text-white font-serif leading-tight">
            Premium amenities for a seamless stay
          </h2>
          <p className="text-gray-300 mt-6 font-light text-sm md:text-base hidden md:block">
            We provide everything you need to feel at home, combined with the luxury of five-star service.
          </p>
        </div>

        {/* Accordion Columns */}
        <div className="flex-1 flex flex-col md:flex-row h-full">
          {hotelData.amenities.map((amenity, index) => {
            const Icon = LucideIcons[amenity.icon];
            const isHovered = hoveredIndex === index;
            
            return (
              <div
                key={index}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                className={`
                  relative flex flex-row md:flex-col items-center justify-start md:justify-center p-6 md:p-0
                  border-b md:border-b-0 md:border-r border-white/20 cursor-pointer overflow-hidden
                  transition-all duration-700 ease-[0.16,1,0.3,1]
                  ${isHovered ? 'md:flex-[1.5]' : 'md:flex-1'}
                `}
              >
                {/* Individual Background Image for the column */}
                {amenity.image && (
                  <img 
                    src={amenity.image} 
                    alt={amenity.name}
                    className={`
                      absolute inset-0 w-full h-full object-cover transition-transform duration-1000 ease-out z-0
                      ${isHovered ? 'scale-110' : 'scale-100'}
                    `}
                  />
                )}
                
                {/* Dark Overlay that lightens on hover */}
                <div 
                  className={`
                    absolute inset-0 z-0 transition-colors duration-700
                    ${isHovered ? 'bg-black/30' : 'bg-black/70'}
                  `}
                />

                {/* Content Overlay */}
                <div className={`
                  flex items-center md:flex-col gap-6 md:gap-8 z-10 transition-transform duration-700 w-full md:w-auto
                  ${isHovered ? 'md:-translate-y-4' : 'md:translate-y-0'}
                `}>
                  {/* Icon Container */}
                  <div className={`
                    p-4 rounded-full transition-all duration-500
                    ${isHovered ? 'bg-orange-500 text-white shadow-[0_0_20px_rgba(249,115,22,0.4)]' : 'bg-white/10 text-white/70 backdrop-blur-md'}
                  `}>
                    <Icon className="w-6 h-6 md:w-8 md:h-8" />
                  </div>
                  
                  {/* Title */}
                  <span className={`
                    text-[10px] md:text-xs tracking-[0.1em] md:tracking-[0.15em] uppercase font-semibold w-full px-1 text-center break-words
                    transition-all duration-500
                    ${isHovered ? 'text-white drop-shadow-md' : 'text-white/70'}
                  `}>
                    {amenity.name}
                  </span>
                </div>

                {/* Subtle highlight gradient on hover */}
                <div 
                  className={`
                    absolute inset-0 bg-gradient-to-t from-orange-500/30 via-transparent to-transparent 
                    transition-opacity duration-700 pointer-events-none z-0
                    ${isHovered ? 'opacity-100' : 'opacity-0'}
                  `} 
                />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Amenities;
